import React, { useRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

export default function AnimateOnScroll({ 
  children, 
  className, 
  animation = 'fadeUp',
  delay = 0,
  duration = 600,
  threshold = 0.15
}) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisible(true);
            }, delay);
            obs.unobserve(el);
          }
        });
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay, threshold]);

  return (
    <Wrapper 
      ref={ref} 
      className={`${className || ''} ${visible ? 'is-visible' : ''}`}
      animation={animation}
      duration={duration}
    >
      {children}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${({ animation, duration }) => {
    const baseStyles = css`
      opacity: 0;
      transition: all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1);
    `;

    switch (animation) {
      case 'fadeUp':
        return css`
          ${baseStyles}
          transform: translateY(30px);
          
          &.is-visible {
            opacity: 1;
            transform: translateY(0);
          }
        `;
      
      case 'fadeDown':
        return css`
          ${baseStyles}
          transform: translateY(-30px);
          
          &.is-visible {
            opacity: 1;
            transform: translateY(0);
          }
        `;
      
      case 'fadeLeft':
        return css`
          ${baseStyles}
          transform: translateX(30px);
          
          &.is-visible {
            opacity: 1;
            transform: translateX(0);
          }
        `;
      
      case 'fadeRight':
        return css`
          ${baseStyles}
          transform: translateX(-30px);
          
          &.is-visible {
            opacity: 1;
            transform: translateX(0);
          }
        `;
      
      case 'scaleUp':
        return css`
          ${baseStyles}
          transform: scale(0.8);
          
          &.is-visible {
            opacity: 1;
            transform: scale(1);
          }
        `;
      
      case 'scaleDown':
        return css`
          ${baseStyles}
          transform: scale(1.2);
          
          &.is-visible {
            opacity: 1;
            transform: scale(1);
          }
        `;
      
      case 'rotateIn':
        return css`
          ${baseStyles}
          transform: rotate(-10deg) scale(0.9);
          
          &.is-visible {
            opacity: 1;
            transform: rotate(0deg) scale(1);
          }
        `;
      
      case 'slideUp':
        return css`
          ${baseStyles}
          transform: translateY(50px);
          
          &.is-visible {
            opacity: 1;
            transform: translateY(0);
          }
        `;
      
      case 'slideDown':
        return css`
          ${baseStyles}
          transform: translateY(-50px);
          
          &.is-visible {
            opacity: 1;
            transform: translateY(0);
          }
        `;
      
      case 'slideLeft':
        return css`
          ${baseStyles}
          transform: translateX(50px);
          
          &.is-visible {
            opacity: 1;
            transform: translateX(0);
          }
        `;
      
      case 'slideRight':
        return css`
          ${baseStyles}
          transform: translateX(-50px);
          
          &.is-visible {
            opacity: 1;
            transform: translateX(0);
          }
        `;
      
      case 'flipUp':
        return css`
          ${baseStyles}
          transform: perspective(1000px) rotateX(90deg);
          
          &.is-visible {
            opacity: 1;
            transform: perspective(1000px) rotateX(0deg);
          }
        `;
      
      case 'flipDown':
        return css`
          ${baseStyles}
          transform: perspective(1000px) rotateX(-90deg);
          
          &.is-visible {
            opacity: 1;
            transform: perspective(1000px) rotateX(0deg);
          }
        `;
      
      case 'zoomIn':
        return css`
          ${baseStyles}
          transform: scale(0.5);
          
          &.is-visible {
            opacity: 1;
            transform: scale(1);
          }
        `;
      
      case 'zoomOut':
        return css`
          ${baseStyles}
          transform: scale(1.5);
          
          &.is-visible {
            opacity: 1;
            transform: scale(1);
          }
        `;
      
      case 'bounceIn':
        return css`
          ${baseStyles}
          transform: scale(0.3);
          
          &.is-visible {
            opacity: 1;
            transform: scale(1);
            animation: bounceIn ${duration}ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
          }
          
          @keyframes bounceIn {
            0% { transform: scale(0.3); }
            50% { transform: scale(1.05); }
            70% { transform: scale(0.9); }
            100% { transform: scale(1); }
          }
        `;
      
      case 'elasticIn':
        return css`
          ${baseStyles}
          transform: scale(0.1) rotate(180deg);
          
          &.is-visible {
            opacity: 1;
            transform: scale(1) rotate(0deg);
            animation: elasticIn ${duration}ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          
          @keyframes elasticIn {
            0% { transform: scale(0.1) rotate(180deg); }
            50% { transform: scale(1.1) rotate(-10deg); }
            100% { transform: scale(1) rotate(0deg); }
          }
        `;
      
      case 'fadeOnly':
        return css`
          ${baseStyles}
          
          &.is-visible {
            opacity: 1;
          }
        `;
      
      default:
        return css`
          ${baseStyles}
          transform: translateY(30px);
          
          &.is-visible {
            opacity: 1;
            transform: translateY(0);
          }
        `;
    }
  }}
`;
